// src/components/projects/ProjectForm.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useFileViewer } from '@/contexts/FileViewerContext';
import { generateId, formatBytes, safeLocalStorage, calculateStorageUsage } from '@/lib/app-utils';
import { saveFileToIDB, getFileFromIDB } from '@/lib/idb-storage';
import { X, FileText, Building, MapPin, Mail, Phone, Paperclip, AlertCircle, Save, Briefcase, User, Database, Loader2, Eye, Inbox } from 'lucide-react';
import FileUpload from '@/components/ui/FileUpload';
import { projectsAPI } from '@/services/projects';

const ProjectForm = ({ initialData, isEdit }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const { openViewer } = useFileViewer();

  const [loading, setLoading] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [storageUsage, setStorageUsage] = useState(0);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    projectName: initialData?.projectName || '',
    companyName: initialData?.companyName || '',
    contactEmail: initialData?.contactEmail || '',
    contactPhone: initialData?.contactPhone || '',
    projectLocation: initialData?.projectLocation || '',
    workType: initialData?.workType || '',
    receivedBy: initialData?.receivedBy || '',
    description: initialData?.description || '',
    attachments: initialData?.attachments || [],
    emailAttachments: initialData?.emailAttachments || [],
    checklist: initialData?.checklist || [],
    status: initialData?.status || 'New Inquiry',
    projectStatus: initialData?.projectStatus || 'Pending',
    companyAddress: initialData?.companyAddress || ''
  });

  useEffect(() => {
    setStorageUsage(calculateStorageUsage());
  }, []);

  useEffect(() => {
    if (initialData && isEdit) {
      console.log('📝 Loading initial data for edit:', initialData);
      
      // ✅ Parse emailAttachments if needed
      const emailAttachments = initialData.emailAttachments || initialData.email_attachments || [];
      
      setFormData({
        ...initialData,
        emailAttachments: Array.isArray(emailAttachments) ? emailAttachments : []
      });
    }
  }, [initialData, isEdit]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.projectName?.trim()) newErrors.projectName = "Project Name is required";
    if (!formData.companyName?.trim()) newErrors.companyName = "Company Name is required";
    if (!formData.workType) newErrors.workType = "Work Type is required";
    if (!formData.receivedBy) newErrors.receivedBy = "Source is required";

    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Invalid email address";
    }
    if (formData.contactPhone && !/^[\d\s\-\+\(\)]{8,}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = "Invalid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle File Upload - منفصل حسب النوع
  const handleFileUpload = async (files, category) => {
    console.log(`📎 Files received for ${category}:`, files);

    const newAttachments = [];
    const targetField = category === 'emails' ? 'emailAttachments' : 'attachments';
    const currentFiles = formData[targetField] || [];

    const fileArray = Array.isArray(files) ? files : [files];

    for (const file of fileArray) {
      // Check duplicates
      if (currentFiles.some(f => f.name === file.name && f.size === file.size)) {
        console.log(`Duplicate file skipped: ${file.name}`);
        continue;
      }

      // Check file size (250MB max)
      if (file.size > 250 * 1024 * 1024) {
        addNotification("File Too Large", `${file.name} exceeds 250MB limit`, "error");
        continue;
      }

      newAttachments.push({
        id: generateId(),
        name: file.name,
        type: file.type,
        size: file.size,
        fileObject: file,
        category: category === 'emails' ? 'emails' : 'general',
        uploadedAt: new Date().toISOString()
      });
    }

    if (newAttachments.length > 0) {
      setFormData(prev => ({
        ...prev,
        [targetField]: [...(prev[targetField] || []), ...newAttachments]
      }));
      addNotification("Files Added", `${newAttachments.length} file(s) ready to upload.`, "info");
      console.log(`✅ Files added to ${category}:`, newAttachments);
    }
  };

  // ✅ Remove Attachment - منفصل حسب النوع
  const removeAttachment = (id, category) => {
    console.log(`🗑️ Removing attachment from ${category}:`, id);
    const targetField = category === 'emails' ? 'emailAttachments' : 'attachments';
    
    setFormData(prev => ({
      ...prev,
      [targetField]: (prev[targetField] || []).filter(f => f.id !== id)
    }));
  };

  const handleViewFile = async (file) => {
    console.log('👁️ Viewing file in form:', file);

    // 1. ملف جديد (لسه ما اترفعش)
    if (file.fileObject) {
      const objUrl = URL.createObjectURL(file.fileObject);
      openViewer(objUrl, file.name);
      return;
    }

    // 2. ملف مرفوع على السيرفر
    const serverPath = file.url || file.file_path || file.path;
    if (serverPath) {
      let fullUrl;

      if (serverPath.startsWith('http')) {
        fullUrl = serverPath;
      } else if (serverPath.startsWith('/')) {
        fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}${serverPath}`;
      } else {
        fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}/${serverPath}`;
      }

      console.log('✅ Opening server file:', fullUrl);

      const isViewable = file.name.match(/\.(pdf|jpg|jpeg|png|gif|webp|svg)$/i);
      if (isViewable) {
        openViewer(fullUrl, file.name);
      } else {
        window.open(fullUrl, '_blank');
      }
      return;
    }

    // 3. ملف في IndexedDB
    if (file.storageType === 'indexedDB' || file.fileId) {
      try {
        const fileId = file.fileId || file.id;
        const fileRecord = await getFileFromIDB(fileId);
        if (fileRecord?.file) {
          const objUrl = URL.createObjectURL(fileRecord.file);
          openViewer(objUrl, file.name);
        } else {
          addNotification("File Not Found", "Could not retrieve file from storage.", "error");
        }
      } catch (e) {
        console.error("❌ IndexedDB error:", e);
        addNotification("Error", "Failed to load file.", "error");
      }
      return;
    }

    // 4. ملف عنده data inline
    if (file.data) {
      openViewer(file.data, file.name);
      return;
    }

    console.error('❌ Could not view file:', file);
    addNotification("Error", "Could not locate file for viewing.", "error");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      addNotification("Validation Error", "Please check the highlighted fields", "error");
      return;
    }

    if (isUploading) {
      addNotification("Wait", "Please wait for files to finish uploading", "warning");
      return;
    }

    setLoading(true);
    setSaveProgress(5);

    try {
      // ✅ فصل الملفات الموجودة مسبقاً عن الجديدة
      const existingGeneralFiles = (formData.attachments || []).filter(f => !f.fileObject);
      const existingEmailFiles = (formData.emailAttachments || []).filter(f => !f.fileObject);
      
      const newGeneralFiles = (formData.attachments || []).filter(f => f.fileObject);
      const newEmailFiles = (formData.emailAttachments || []).filter(f => f.fileObject);

      setSaveProgress(20);

      // ✅ تجهيز البيانات
      const apiData = {
        project_name: formData.projectName?.trim() || null,
        company_name: formData.companyName?.trim() || null,
        company_address: formData.companyAddress?.trim() || null,
        contact_email: formData.contactEmail?.trim() || null,
        contact_phone: formData.contactPhone?.trim() || null,
        project_location: formData.projectLocation?.trim() || null,
        work_type: formData.workType || null,
        received_by: formData.receivedBy || null,
        description: formData.description?.trim() || null,
        status: formData.status || 'New Inquiry',
        project_status: formData.projectStatus || 'Pending',
        checklist: JSON.stringify(formData.checklist || []),
        attachments: JSON.stringify(existingGeneralFiles.map(f => ({
          id: f.id,
          name: f.name,
          type: f.type,
          size: f.size,
          category: 'general',
          uploadedAt: f.uploadedAt,
          url: f.url || f.file_path || f.path || null
        }))),
        email_attachments: JSON.stringify(existingEmailFiles.map(f => ({
          id: f.id,
          name: f.name,
          type: f.type,
          size: f.size,
          category: 'emails',
          uploadedAt: f.uploadedAt,
          url: f.url || f.file_path || f.path || null
        })))
      };

      setSaveProgress(40);

      console.log('📤 Sending to API:', apiData);
      console.log('📎 New general files:', newGeneralFiles.length);
      console.log('📧 New email files:', newEmailFiles.length);
      console.log('📂 Existing general:', existingGeneralFiles.length);
      console.log('📬 Existing email:', existingEmailFiles.length);

      setSaveProgress(60);

      let response;
      const allNewFiles = [...newGeneralFiles, ...newEmailFiles];
      
      if (isEdit && initialData?.id) {
        // ✅ Update mode - بعت الملفات الجديدة كمان
        response = await projectsAPI.update(initialData.id, apiData, allNewFiles);
        addNotification("Success", "Project updated successfully", "success");
        setTimeout(() => navigate(`/projects/${initialData.id}`), 500);
      } else {
        // ✅ Create mode
        response = await projectsAPI.create(apiData, allNewFiles);
        addNotification("Success", `Project created with ${allNewFiles.length} file(s)`, "success");
        setTimeout(() => navigate(`/projects/${response.data.id}`), 500);
      }

      setSaveProgress(100);

    } catch (error) {
      console.error('❌ API Error:', error);
      addNotification("Error", error.message || "Failed to save project", "error");
    } finally {
      setLoading(false);
      setSaveProgress(0);
    }
  };

  // ✅ Helper functions
  const getTotalSize = (category) => {
    const files = category === 'emails' ? (formData.emailAttachments || []) : (formData.attachments || []);
    if (!Array.isArray(files)) return 0;
    return files.reduce((acc, curr) => acc + (curr.size || 0), 0);
  };

  const inputClass = "h-10 bg-slate-50 border-slate-200 focus:border-safety-orange focus:ring-safety-orange/20 transition-all";
  const sectionTitleClass = "text-lg font-bold text-slate-800 flex items-center gap-2 mb-4";
  const iconClass = "w-5 h-5 text-safety-orange";
  const labelClass = "text-sm font-medium text-slate-700 mb-1.5 block";

  return (
    <form onSubmit={handleSubmit} className="animate-in fade-in duration-500 pb-10 max-w-7xl mx-auto">
      <div className="rounded-xl border border-stone-grey/30 text-navy-steal shadow-navy-steal/10 border-t-4 border-t-safety-orange shadow-lg bg-white overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Form Fields */}
            <div className="space-y-8">
              {/* Project Identification */}
              <div>
                <h3 className={sectionTitleClass}>
                  <Building className={iconClass} />
                  Project Identification
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1">
                    <Label className={labelClass}>
                      Project Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="e.g. Villa Demolition"
                      value={formData.projectName}
                      onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                      className={inputClass}
                    />
                    {errors.projectName && <p className="text-xs text-red-500 mt-1">{errors.projectName}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className={labelClass}>
                      Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="e.g. Al Futtaim"
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      className={inputClass}
                    />
                    {errors.companyName && <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>}
                  </div>
                </div>
              </div>

              <div className="h-px bg-slate-100 w-full" />

              {/* Contact Information */}
              <div>
                <h3 className={sectionTitleClass}>
                  <User className={iconClass} />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1">
                    <Label className={labelClass}>Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="client@example.com"
                        value={formData.contactEmail}
                        onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                    {errors.contactEmail && <p className="text-xs text-red-500 mt-1">{errors.contactEmail}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className={labelClass}>Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="tel"
                        placeholder="+971 50 123 4567"
                        value={formData.contactPhone}
                        onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                    {errors.contactPhone && <p className="text-xs text-red-500 mt-1">{errors.contactPhone}</p>}
                  </div>
                </div>
              </div>

              <div className="h-px bg-slate-100 w-full" />

              {/* Specifications */}
              <div>
                <h3 className={sectionTitleClass}>
                  <Briefcase className={iconClass} />
                  Specifications
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="space-y-1">
                    <Label className={labelClass}>Location</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="City/Area"
                        value={formData.projectLocation}
                        onChange={(e) => setFormData(prev => ({ ...prev, projectLocation: e.target.value }))}
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className={labelClass}>
                      Work Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.workType} onValueChange={(val) => setFormData(prev => ({ ...prev, workType: val }))}>
                      <SelectTrigger className={inputClass}>
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Full Demolition">Full Demolition</SelectItem>
                        <SelectItem value="Partial Demolition">Partial Demolition</SelectItem>
                        <SelectItem value="Stripout">Stripout</SelectItem>
                        <SelectItem value="Concrete Cutting">Concrete Cutting</SelectItem>
                        <SelectItem value="Coring Work">Coring Work</SelectItem>
                        <SelectItem value="Concrete Scanning">Concrete Scanning</SelectItem>
                        <SelectItem value="Excavation">Excavation</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.workType && <p className="text-xs text-red-500 mt-1">{errors.workType}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className={labelClass}>
                      Received By <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.receivedBy} onValueChange={(val) => setFormData(prev => ({ ...prev, receivedBy: val }))}>
                      <SelectTrigger className={inputClass}>
                        <SelectValue placeholder="Select Source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Internet">Internet</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.receivedBy && <p className="text-xs text-red-500 mt-1">{errors.receivedBy}</p>}
                  </div>
                </div>

                <div className="mt-5 space-y-1">
                  <Label className={labelClass}>Description / Notes</Label>
                  <Textarea
                    placeholder="Enter any additional details about the project here..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="min-h-[100px] bg-slate-50 border-slate-200 focus:border-safety-orange focus:ring-safety-orange/20"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Attachments */}
            <div className="flex flex-col h-full">
              <div className="flex-1 space-y-8">
                <div className="flex items-center justify-between">
                  <h3 className={`${sectionTitleClass} mb-0`}>
                    <Paperclip className={iconClass} />
                    Project Attachments
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    <Database className="w-3 h-3" />
                    <span>Local Usage: {formatBytes(storageUsage)}</span>
                  </div>
                </div>

                <div className="bg-slate-50/50 rounded-xl border border-slate-100 p-6 space-y-8">
                  {/* ✅ General Documents */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <Label className={`${labelClass} flex items-center gap-2`}>
                        <FileText className="w-4 h-4 text-safety-orange" />
                        General Documents
                      </Label>
                      <span className="text-xs text-slate-400">{formatBytes(getTotalSize('general'))} used</span>
                    </div>

                    <FileUpload
                      onFileSelect={(files) => handleFileUpload(files, 'general')}
                      onUploadStatusChange={setIsUploading}
                      label="Drop files here or click to upload (Max 250MB each)"
                      maxSizeMB={250}
                      multiple
                    />

                    {(formData.attachments || []).map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-slate-500">{formatBytes(file.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleViewFile(file)} title="View File">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeAttachment(file.id, 'general')} title="Remove">
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="h-px bg-slate-200/50" />

                  {/* ✅ Email Correspondence */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <Label className={`${labelClass} flex items-center gap-2`}>
                        <Inbox className="w-4 h-4 text-safety-orange" />
                        Email Correspondence
                      </Label>
                      <span className="text-xs text-slate-400">{formatBytes(getTotalSize('emails'))} used</span>
                    </div>

                    <FileUpload
                      onFileSelect={(files) => handleFileUpload(files, 'emails')}
                      onUploadStatusChange={setIsUploading}
                      label="Drop files here or click to upload (Max 250MB each)"
                      maxSizeMB={250}
                      multiple
                    />

                    {(formData.emailAttachments || []).map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded border border-blue-200 hover:bg-blue-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Inbox className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-slate-500">{formatBytes(file.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleViewFile(file)} title="View File">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeAttachment(file.id, 'emails')} title="Remove">
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4 pt-8 mt-auto border-t border-slate-200 pb-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/projects')}
                  className="w-32"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-40 gap-2 bg-safety-orange hover:bg-orange-700 font-semibold shadow-md shadow-orange-200"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {saveProgress}%
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Project
                    </>
                  )}
                </Button>
              </div>

              {/* Progress Bar */}
              {loading && (
                <div className="mt-4">
                  <Progress value={saveProgress} className="h-2" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProjectForm;
